/** GOOD LUCK — the one screen a fan sees straight after they save their star.
 *
 *  It exists so Save never looks like it did nothing: the game, the winner they
 *  picked, the star they picked, and what to do when the next one goes in. It
 *  reads only what the fan screen already has — the winner-pick block and the
 *  saved star — and writes nothing of its own. The winner buttons here ARE the
 *  existing one-tap pick, so a fan who has not chosen yet can choose right here.
 */
import { Check, PartyPopper } from 'lucide-react';
import { WinnerPick } from './GoalWinnerPick';

const TEAM_COLOURS = {
    MTL: '#AF1E2D', TOR: '#00205B', BUF: '#003087', BOS: '#FFB81C', OTT: '#C8102E',
    WSH: '#C8102E', WPG: '#041E42', STL: '#002F87', CHI: '#CF0A2C', DAL: '#006847',
    COL: '#6F263D', TBL: '#002868', NJD: '#CE1126', NYR: '#0038A8', NYI: '#00539B',
    PIT: '#FCB514', PHI: '#F74902', CAR: '#CE1126', FLA: '#C8102E', DET: '#CE1126',
    NSH: '#FFB81C', MIN: '#154734', SJS: '#006D75', LAK: '#111111', VGK: '#B4975A',
    EDM: '#FF4C00', CGY: '#D2001C', VAN: '#00205B', SEA: '#99D9D9', ANA: '#F47A38',
    UTA: '#71AFE5', CBJ: '#002654',
};
const colourOf = (team) => TEAM_COLOURS[(team.abbrev || '').toUpperCase()]
    || (team.side === 'home' ? '#1D4ED8' : '#B45309');

/** The feed's start time, read in the venue's own clock. A value with no time
 *  part in it at all (a bare date, or nothing) shows no time — never a made-up
 *  midnight. */
const gameTime = (iso, lang) => {
    if (!iso || !String(iso).includes('T')) return '';
    try {
        const when = new Date(iso);
        if (Number.isNaN(when.getTime())) return '';
        return when.toLocaleTimeString(lang === 'fr' ? 'fr-CA' : 'en-CA',
                                       { hour: 'numeric', minute: '2-digit',
                                         timeZone: 'America/Toronto' });
    } catch (_e) { return ''; }
};

export const GoalGoodLuck = ({ star, winner, cardId, deviceId, lang, t, txt, c, ty,
                              primaryBtn, panel, panelCss, onDone, onRefresh,
                              pointsOn = false }) => {
    const teams = winner?.teams || [];
    const mine = winner?.my_pick || null;
    const versus = teams.length === 2
        ? `${teams.find((x) => x.side === 'away')?.abbrev || ''} vs ${teams.find((x) => x.side === 'home')?.abbrev || ''}`
        : (winner?.game_label || '');
    const when = gameTime(winner?.start_time_utc, lang);

    return (
        <div className={panel} style={panelCss} data-testid="goal-good-luck">
            <PartyPopper size={26} className="mx-auto mb-2" style={{ color: c.accent }} />
            <p className="text-2xl font-black mb-3" style={ty.headline}
               data-testid="goal-good-luck-title">{t('goalGame.goodLuckTitle')}</p>
            {/* 1b — the big line, the last thing registration says. */}
            <p className="mb-5 text-lg font-bold leading-snug sm:text-xl"
               data-testid="goal-good-luck-big">{t('goalGame.goodLuckBig')}</p>

            {/* 2 — the game itself, with its time when the feed has one. */}
            {versus && (
                <div className="mb-4" data-testid="goal-good-luck-game">
                    <p className="text-[11px] uppercase tracking-wider opacity-55">
                        {t('goalGame.goodLuckGame')}
                    </p>
                    <p className="text-lg font-bold">{versus}</p>
                    {when && <p className="text-xs opacity-70" data-testid="goal-good-luck-time">{when}</p>}
                </div>
            )}

            {/* 3 — the winner they picked, highlighted. Not picked yet? The same
                   one-tap pick, right here. */}
            {teams.length === 2 && (
                <div className="mb-4" data-testid="goal-good-luck-winner">
                    <p className="text-[11px] uppercase tracking-wider opacity-55 mb-1.5">
                        {t('goalGame.goodLuckWinner')}
                    </p>
                    {mine ? (
                        <div className="grid grid-cols-2 gap-2">
                            {teams.map((team) => (
                                <div key={team.side}
                                     data-testid={`goal-good-luck-team-${team.side}`}
                                     className={`flex min-h-[52px] flex-col items-center justify-center rounded-xl px-2 py-2 text-white ${
                                         mine === team.side ? 'ring-4 ring-white/80' : 'opacity-45'}`}
                                     style={{ backgroundColor: colourOf(team) }}>
                                    <span className="flex items-center gap-1.5 text-base font-bold">
                                        {team.abbrev}
                                        {mine === team.side && <Check size={15} />}
                                    </span>
                                    <span className="text-[11px] font-semibold opacity-90">{team.name}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <WinnerPick cardId={cardId} block={winner} t={t} c={c} startOpen
                                    showPoints={pointsOn}
                                    deviceId={deviceId} onPicked={() => onRefresh?.()} />
                    )}
                </div>
            )}

            {/* 4 — the star they just saved. */}
            <div className="mb-4" data-testid="goal-good-luck-star">
                <p className="text-[11px] uppercase tracking-wider opacity-55">
                    {t('goalGame.goodLuckStar')}
                </p>
                <p className="text-lg font-bold" data-testid="goal-good-luck-star-name">
                    {star
                        ? `${star.name}${star.number ? ` #${star.number}` : ''}`
                        : t('goalGame.goodLuckNoStar')}
                </p>
            </div>

            {/* 5 — what to do when the next one goes in, in this league's word. */}
            <p className="mb-5 rounded-xl border border-white/15 bg-black/25 px-3 py-2 text-sm"
               data-testid="goal-good-luck-message">
                {txt('goodLuckTapCard')}
            </p>

            {/* 6 — back to the main screen. */}
            <button type="button" onClick={onDone} style={primaryBtn}
                    data-testid="goal-good-luck-done"
                    className="w-full rounded-xl font-bold py-3 shadow-lg">
                {t('goalGame.goodLuckDone')}
            </button>
        </div>
    );
};

export default GoalGoodLuck;
